<template>
  <main class="main-content flex-grow-1 p-4 overflow-auto mt-6" style="background-color: #f8f9fa">
    <h2 class="text-header-page">Transaction</h2>

    <!-- Sandbox Mode Alert -->
    <div v-if="environment === 'sandbox' & !shouldHideApiTab" class="alert alert-warning mb-4 text-white" role="alert">
      <i class="fas fa-exclamation-triangle me-2"></i>
      You are currently on sandbox mode, where all operations are testing
      purpose only and do not affect live data
    </div>

    <div class="row justify-content-center">
      <div class="col-12 px-0">
        <!-- Tab Navigation -->
        <div class="card border-0 m-4">
          <div class="card-body p-0">
            <div class="tab-navigation">
              <nav class="nav nav-tabs border-bottom-0" id="transactionTabs" role="tablist">
                <button class="nav-link" :class="{ active: activeTab === 'merchant' }" @click="activeTab = 'merchant'"
                  type="button" role="tab">
                  Merchant
                </button>
                <button v-if="!shouldHideApiTab" class="nav-link" :class="{ active: activeTab === 'api' }" @click="activeTab = 'api'"
                  type="button" role="tab">
                  API
                </button>
              </nav>
            </div>
          </div>
        </div>

        <!-- Merchant Tab Content -->
        <div v-if="activeTab === 'merchant'">
          <!-- Transaction Table Container -->
          <div class="card border-0 m-4">
            <div class="card-body p-0">
              <div class="transaction-section p-0">
                <div class="transaction-table">
                  <!-- Skeleton Loader -->
                  <div v-if="loading" class="skeleton-container">
                    <!-- Header Controls Skeleton -->
                    <div class="transaction-table-header d-flex align-items-center w-100 flex-wrap p-4"
                      style="gap: 8px">
                      <div class="flex-grow-1">
                        <div class="row g-2 align-items-center">
                          <div class="col-auto">
                            <div class="skeleton-input skeleton-search"></div>
                          </div>
                          <div class="col-auto">
                            <div class="skeleton-input skeleton-select"></div>
                          </div>
                          <div class="col-auto">
                            <div class="skeleton-input skeleton-date"></div>
                          </div>
                        </div>
                      </div>
                      <div class="d-flex align-items-center ms-auto pe-0">
                        <div class="skeleton-button skeleton-export"></div>
                      </div>
                    </div>

                    <!-- Table Skeleton -->
                    <div class="skeleton-table">
                      <!-- Table Header -->
                      <div class="skeleton-table-header">
                        <div class="skeleton-cell skeleton-cell-header" v-for="i in 6" :key="i"></div>
                      </div>
                      <!-- Table Rows -->
                      <div class="skeleton-table-row" v-for="i in 5" :key="i">
                        <div class="skeleton-cell" v-for="j in 6" :key="j"></div>
                      </div>
                    </div>
                  </div>

                  <!-- Actual Content -->
                  <div v-else>
                    <div class="transaction-table-header d-flex align-items-center w-100 flex-wrap p-4"
                      style="gap: 8px">
                      <div class="flex-grow-1">
                        <div class="row g-2 align-items-center">
                          <div class="col-auto">
                            <div class="input-wrapper search-input-wrapper">
                              <span class="input-icon icon-left">
                                <i class="fa fa-search"></i>
                              </span>
                              <input type="text" placeholder="Search Ref ID" v-model="search"
                                class="search-input styled-input" />
                            </div>
                          </div>
                          <div class="col-auto">
                            <div class="input-wrapper status-input-wrapper">
                              <select v-model="status" class="status-select styled-input">
                                <option value="" selected>Status</option>
                                <option v-for="option in statusOptions" :key="option.value" :value="option.value">
                                  {{ option.text }}
                                </option>
                              </select>
                              <span class="input-icon icon-right select-arrow">
                                <i class="fa fa-chevron-down"></i>
                              </span>
                            </div>
                          </div>
                          <div class="col-auto">
                            <SwiftDatePicker v-model="dateRange" placeholder="Select Date Range" />
                          </div>
                        </div>
                      </div>
                      <div class="d-flex align-items-center ms-auto pe-0 export-btn-responsive" style="min-width: 0">
                        <argon-button variant="export" @click="exportData()">
                          Export
                        </argon-button>
                      </div>
                    </div>

                    <SwiftDataTable :headers="tableHeaders" :items="transactions" :rows-per-page="pagination.per_page"
                      hide-footer class="transaction-data-table">
                      <template #item-batch_id="row">
                        <a class="batch-link" @click="viewDetail(row)">{{
                          row.batch_id
                          }}</a>
                      </template>
                      <template #item-amount="{ amount }">
                        IDR {{ formatNumber(amount) }}
                      </template>

                      <template #item-status="{ status }">
                        <span :class="['status-badge', statusClass(status)]">
                          {{ status }}
                        </span>
                      </template>

                      <template #item-action="row">
                        <button class="view-detail-btn" @click="viewDetail(row)">
                          View Detail
                        </button>
                      </template>
                    </SwiftDataTable>

                    <!-- Pagination Controls -->
                    <div class="pagination-controls d-flex align-items-center justify-content-between mt-4"
                      v-if="!loading && transactions.length > 0">
                      <div class="per-page-control d-flex align-items-center">
                        <span class="me-2">Show:</span>
                        <select class="form-select form-select-sm" v-model="pagination.per_page"
                          @change="onPerPageChange">
                          <option value="10">10</option>
                          <option value="20">20</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                      </div>

                      <div class="d-flex align-items-center">
                        <span class="pagination-info me-3">
                          Showing {{ displayStart }} to {{ displayEnd }} of
                          {{ totalFiltered }} entries
                        </span>

                        <nav aria-label="Transaction pagination">
                          <ul class="pagination mb-0">
                            <li class="page-item" :class="{ disabled: !hasPrevPage }">
                              <button class="page-link" @click="onPreviousPageFiltered" :disabled="!hasPrevPage">
                                <i class="fas fa-chevron-left"></i>
                              </button>
                            </li>
                            <li class="page-item">
                              <span class="page-link bg-primary text-white">{{
                                pagination.current_page
                                }}</span>
                            </li>
                            <li class="page-item" :class="{ disabled: !hasNextPage }">
                              <button class="page-link" @click="onNextPageFiltered" :disabled="!hasNextPage">
                                <i class="fas fa-chevron-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- API Tab Content -->
        <div v-if="activeTab === 'api' && !shouldHideApiTab">
          <ApiTransactionListComponent :loading="loading" />
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import ArgonButton from "@/components/ArgonButton.vue";
import SwiftDatePicker from "@/components/SwiftDatePicker.vue";
import SwiftDataTable from "@/components/SwiftDataTable.vue";
import ApiTransactionListComponent from "./ApiTransactionListComponent.vue";
import { getListBatch } from "@/api/transaction";
import { isAkunDemo } from "@/utils/role-utils";

export default {
  name: "TransactionView",
  components: {
    ArgonButton,
    SwiftDatePicker,
    SwiftDataTable,
    ApiTransactionListComponent,
  },
  data() {
    return {
      activeTab: "merchant", // Default tab
      search: "",
      status: "",
      statusOptions: [
        { text: "All Status", value: "" },
        { text: "Draft", value: "Draft" },
        { text: "Waiting For Payment", value: "Waiting for Payment" },
        { text: "Processing", value: "Processing" },
        { text: "Cancel", value: "Cancelled" },
        { text: "Partial Success", value: "Partial Success" },
        { text: "Success", value: "Success" },
        { text: "Failed", value: "Failed" },
      ],
      dateRange: null,
      pagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        next_page_url: null,
        prev_page_url: null,
      },
      tableHeaders: [
        { text: "No. ID", value: "batch_id" },
        { text: "Date & Time", value: "batch_datetime" },
        { text: "Total Amount", value: "grand_total_amount" },
        { text: "Total Recipient", value: "total_recipient" },
        { text: "Status", value: "status" },
        { text: "Action", value: "action" },
      ],
      transactions: [],
      selectedDate: [],
      loading: false,
    };
  },
  computed: {
    environment() {
      return this.$store.getters.currentEnvironment;
    },
    isBiUser() {
      return this.$store.getters["auth/isBiUser"];
    },
    shouldHideApiTab() {
      return isAkunDemo() || this.isBiUser;
    },

    // Check if any filters are active
    hasActiveFilters() {
      return !!(
        this.search ||
        this.status ||
        (this.dateRange &&
          Array.isArray(this.dateRange) &&
          this.dateRange.length === 2 &&
          this.dateRange[0] &&
          this.dateRange[1])
      );
    },

    // Get all filtered data (without pagination)
    allFilteredTransactions() {
      let filtered = this.transactions;

      // Filter by search (batch_id)
      if (this.search) {
        filtered = filtered.filter(
          (t) =>
            t.batch_id &&
            t.batch_id.toLowerCase().includes(this.search.toLowerCase())
        );
      }

      // Filter by status
      if (this.status) {
        filtered = filtered.filter(
          (item) =>
            item.status &&
            item.status.toLowerCase() === this.status.toLowerCase()
        );
      }

      // Filter by date range
      if (
        this.dateRange &&
        Array.isArray(this.dateRange) &&
        this.dateRange.length === 2 &&
        this.dateRange[0] &&
        this.dateRange[1]
      ) {
        const startDate = this.formatDateToFilter(new Date(this.dateRange[0]));
        const endDate = this.formatDateToFilter(new Date(this.dateRange[1]));

        filtered = filtered.filter((item) => {
          const itemDateTime = item.batch_datetime;
          return itemDateTime >= startDate && itemDateTime <= endDate;
        });
      }
      return filtered;
    },

    // Get filtered data for current page display
    filteredTransactions() {
      // Always use server-side data (filters are applied on server)
      return this.transactions;
    },

    // Calculate display values for pagination info
    totalFiltered() {
      // Always use server pagination total (filters are applied on server)
      return this.pagination.total;
    },

    displayStart() {
      if (this.totalFiltered === 0) return 0;
      return Math.min(
        (this.pagination.current_page - 1) * this.pagination.per_page + 1,
        this.totalFiltered
      );
    },

    displayEnd() {
      if (this.totalFiltered === 0) return 0;
      return Math.min(
        this.pagination.current_page * this.pagination.per_page,
        this.totalFiltered
      );
    },

    // Calculate if there are previous/next pages
    hasPrevPage() {
      return this.pagination.current_page > 1;
    },

    hasNextPage() {
      // Always use server pagination info (filters are applied on server)
      return !!this.pagination.next_page_url;
    },

    totalPages() {
      return Math.ceil(this.totalFiltered / this.pagination.per_page);
    },
  },
  mounted() {
    this.fetchBatchData();
  },
  methods: {
    formatNumber(number) {
      return Number(number).toLocaleString("en-US");
    },
    stripTime(date) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    },
    viewDetail(row) {
      if (row.status && row.status.toLowerCase() === "draft") {
        // Arahkan ke Transfer.vue step PreviewEntriesStep
        this.$router.push({
          path: "/transfer",
          query: {
            currentStep: 2,
            batchId: row.uuid || row.id || row.batch_id,
          },
        });
      } else {
        // Default: detail batch - gunakan identifier yang tersedia
        const id = row.uuid || row.id || row.batch_id;
        this.$router.push(`/transaction/${id}`);
      }
    },
    async exportData() {
      try {
        this.loading = true;

        // Get all data with current filters applied
        const allFilteredData = await this.fetchAllFilteredData();

        if (!allFilteredData || allFilteredData.length === 0) {
          this.$swal("No data to export");
          return;
        }

        let csv = "Batch ID|Date&Time|TotalAmount|TotalReceipt|Status" + "\n";

        allFilteredData.forEach((row) => {
          csv += row.batch_id + "|";
          csv += row.batch_datetime + "|";
          csv += row.grand_total_amount + "|";
          csv += row.total_recipient + "|";
          csv += row.status + "|";
          csv += "\n";
        });

        // Generate informative filename
        const now = new Date();
        const dateStr = now.toISOString().split("T")[0]; // YYYY-MM-DD format
        const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, ""); // HHMMSS format

        // Build filename with filter info
        let filterInfo = "";
        if (this.search)
          filterInfo += `_search-${this.search.substring(0, 10)}`;
        if (this.status)
          filterInfo += `_status-${this.status.replace(/\s+/g, "-").toLowerCase()}`;
        if (this.dateRange && this.dateRange.length === 2) {
          const startDate = new Date(this.dateRange[0]);
          const endDate = new Date(this.dateRange[1]);
          filterInfo += `_from-${startDate.toISOString().split("T")[0]}_to-${endDate.toISOString().split("T")[0]}`;
        }

        const filename = `merchant-transactions${filterInfo}_${dateStr}_${timeStr}.csv`;

        const anchor = document.createElement("a");
        anchor.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
        anchor.target = "_blank";
        anchor.download = filename;
        anchor.click();
      } catch (error) {
        console.error("Error exporting data:", error);
        this.$swal("Error exporting data. Please try again.");
      } finally {
        this.loading = false;
      }
    },

    // Fetch all data with filters applied for export
    async fetchAllFilteredData() {
      try {
        const params = {
          page: 1,
          per_page: 10000, // Large number to get all data
        };

        // Add current filters to params
        if (this.search && this.search.trim() !== "") {
          params.batch_id = this.search.trim();
        }

        if (this.status && this.status.trim() !== "") {
          params.status = this.status.trim();
        }

        if (
          this.dateRange &&
          Array.isArray(this.dateRange) &&
          this.dateRange.length === 2 &&
          this.dateRange[0] &&
          this.dateRange[1]
        ) {
          const start_date = this.formatDateToFilter(
            new Date(this.dateRange[0])
          );
          const end_date = this.formatDateToFilter(new Date(this.dateRange[1]));
          params.date_range = `${start_date} - ${end_date}`;
        }

        const response = await getListBatch(params);

        if (
          response &&
          response.status === "success" &&
          response.data &&
          response.data.data
        ) {
          return response.data.data;
        } else {
          console.warn("Export API response structure unexpected:", response);
          return [];
        }
      } catch (error) {
        console.error("Error fetching filtered data for export:", error);
        throw error;
      }
    },
    statusClass(status) {
      switch (status.toLowerCase()) {
        case "success":
          return "success";
        case "failed":
          return "failed";
        case "draft":
          return "draft";
        case "waiting for payment":
          return "waiting";
        case "cancelled":
          return "cancelled";
        case "processing":
          return "processing";
        default:
          return "";
      }
    },
    async fetchBatchData(page = 1, perPage = 10) {
      this.loading = true;
      try {
        const params = {
          page: page,
          per_page: perPage,
        };

        if (this.search && this.search.trim() !== "") {
          params.batch_id = this.search.trim();
        }

        if (this.status && this.status.trim() !== "") {
          params.status = this.status.trim();
        }

        if (
          this.dateRange &&
          Array.isArray(this.dateRange) &&
          this.dateRange.length === 2 &&
          this.dateRange[0] &&
          this.dateRange[1]
        ) {
          const start_date = this.formatDateToFilter(
            new Date(this.dateRange[0])
          );
          const end_date = this.formatDateToFilter(new Date(this.dateRange[1]));
          params.date_range = `${start_date} - ${end_date}`;
        }

        const response = await getListBatch(params);
        if (response && response.status === "success" && response.data) {
          // Update transactions data
          this.transactions = response.data.data || [];

          // Update pagination data if available
          if (response.data.pagination) {
            this.pagination = response.data.pagination;
          } else {
            // If backend doesn't provide pagination info yet, update only what we know
            this.pagination.current_page = page;
            this.pagination.per_page = perPage;
            this.pagination.total = this.transactions.length;

            // Calculate if we should have prev/next buttons
            // This is a fallback if backend doesn't provide pagination
            this.pagination.prev_page_url = page > 1 ? "#" : null;
            this.pagination.next_page_url =
              this.transactions.length >= perPage ? "#" : null;
          }
        } else {
          this.transactions = [];
        }
      } catch (error) {
        this.transactions = [];
        this.$swal("Failed to fetch batch data");
      } finally {
        this.loading = false;
      }
    },
    formatDateToFilter(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${day}/${month}/${year}`;
    },

    // Pagination methods
    onPreviousPage() {
      if (this.pagination.current_page > 1) {
        this.fetchBatchData(
          this.pagination.current_page - 1,
          this.pagination.per_page
        );
      }
    },
    onNextPage() {
      if (this.pagination.current_page < this.pagination.last_page) {
        this.fetchBatchData(
          this.pagination.current_page + 1,
          this.pagination.per_page
        );
      }
    },
    onPerPageChange() {
      // Always use server-side pagination
      this.fetchBatchData(1, this.pagination.per_page);
    },

    // Handle pagination for filtered data
    onPreviousPageFiltered() {
      if (this.hasPrevPage) {
        // Always use server-side pagination
        this.fetchBatchData(
          this.pagination.current_page - 1,
          this.pagination.per_page
        );
      }
    },

    onNextPageFiltered() {
      if (this.hasNextPage) {
        // Always use server-side pagination
        this.fetchBatchData(
          this.pagination.current_page + 1,
          this.pagination.per_page
        );
      }
    },
  },
  watch: {
    shouldHideApiTab(hide) {
      if (hide && this.activeTab === "api") {
        this.activeTab = "merchant";
      }
    },
    search: function () {
      // Reset to page 1 when search changes
      this.pagination.current_page = 1;
      this.fetchBatchData(1, this.pagination.per_page);
    },
    status: function () {
      // Reset to page 1 when status changes
      this.pagination.current_page = 1;
      this.fetchBatchData(1, this.pagination.per_page);
    },
    dateRange: {
      handler() {
        // Reset to page 1 when date range changes
        this.pagination.current_page = 1;
        this.fetchBatchData(1, this.pagination.per_page);
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.table-container {
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.transaction-data-table {
  margin: 1rem 0;
}

.transaction-data-table :deep(th),
.transaction-data-table :deep(td) {
  padding: 12px 16px !important;
}

.transaction-data-table :deep(th) {
  font-weight: 600;
  color: #495057;
  background-color: #f8f9fa;
}

.pagination-controls {
  padding: 1rem;
  background-color: #fff;
  border-radius: 0 0 8px 8px;
  margin-top: 0 !important;
}

/* Tab Navigation Styles */
.tab-navigation {
  padding: 1rem 1rem 0 1rem;
  border-bottom: 1px solid #eaecf0;
}

.nav-tabs {
  border-bottom: none;
}

.nav-tabs .nav-link {
  border: none;
  border-radius: 0;
  padding: 12px 24px;
  color: #6b7280;
  font-weight: 500;
  background: transparent;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease-in-out;
}

.nav-tabs .nav-link:hover {
  color: #128050;
  border-color: transparent;
  background: transparent;
}

.nav-tabs .nav-link.active {
  color: #128050;
  background: transparent;
  border-bottom: 2px solid #128050;
  font-weight: 600;
}

.nav-tabs .nav-link:focus {
  outline: none;
  box-shadow: none;
}

@media (max-width: 768px) {
  .transaction-table-header {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .export-btn-responsive {
    width: 100% !important;
    margin-top: 12px;
    justify-content: flex-end !important;
  }

  .export-btn-responsive>.btn {
    width: 100%;
  }
}

/* Keep styles for the overall layout and other elements */
.transaction-table {
  width: 100%;
}

.transaction-table-header {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  align-items: center;
}

/* Shared wrapper style (if not fully encapsulated in BaseDateRangePicker) */
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

/* Base styling for inputs/select (used by search and status) */
.styled-input {
  height: 40px;
  border-radius: 100px !important;
  border: 1px solid #e2e8f0 !important;
  background-color: #f8fafc !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  font-size: 14px !important;
  color: #4a5568;
  box-sizing: border-box;
  width: 100%;
  transition: border-color 0.2s ease-in-out;
}

.styled-input:focus {
  border-color: #4299e1 !important;
  outline: none;
  box-shadow: 0 0 0 1px rgba(66, 153, 225, 0.5);
}

.styled-input::placeholder {
  color: #a0aec0;
  opacity: 1;
}

/* Search Input */
.search-input-wrapper {
  flex-basis: 250px;
}

.search-input.styled-input {
  padding-left: 40px !important;
}

/* Status Select */
.status-input-wrapper {
  min-width: 150px;
}

.status-select.styled-input {
  padding-left: 12px !important;
  padding-right: 35px !important;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  cursor: pointer;
}

.status-select option[disabled] {
  color: #a0aec0;
}

/* Icon positioning (shared) */
.input-icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.icon-left {
  left: 12px;
}

.icon-right {
  /* Still used by select */
  right: 12px;
}

.select-arrow {
  /* Still used by select */
  pointer-events: none;
}

/* Export Button */
.export-btn {
  margin-left: auto;
  height: 40px;
  display: inline-flex;
  align-items: center;
}

/* Export Button */
.export-btn {
  margin-left: auto;
  height: 40px;
  display: inline-flex;
  align-items: center;
}

.batch-link {
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
}

.text-right {
  text-align: right;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  display: inline-block;
  text-align: center;
}

.status-badge.success {
  background-color: #dcfce7;
  color: #15803d;
}

.status-badge.failed {
  background-color: #fee2e2;
  color: #dc2626;
}

.status-badge.draft {
  background-color: #f3f4f6;
  color: #4b5563;
}

.status-badge.waiting {
  background-color: #fff7ed;
  color: #ea580c;
}

.status-badge.cancelled {
  background-color: #f3f4f6;
  color: #ea580c;
}

.status-badge.processing {
  background-color: #f3f4f6;
  color: #128050;
}

.view-detail-btn {
  display: flex;
  text-align: center;
  justify-content: center;
  background-color: white;
  color: black;
  border: 1px solid #dae0e2;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;
  white-space: nowrap;
}

.alert-warning {
  border-left: 4px solid #ffc107;
  background-color: #fff8e1;
  border-radius: 6px;
}

/* Skeleton Loader Styles */
.skeleton-container {
  padding: 20px;
  background-color: #f0f2f5;
  border-radius: 8px;
  margin-bottom: 20px;
}

.skeleton-input {
  height: 40px;
  background-color: #e0e0e0;
  border-radius: 8px;
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-search {
  width: 200px;
}

.skeleton-select {
  width: 120px;
}

.skeleton-date {
  width: 180px;
}

.skeleton-button {
  height: 40px;
  background-color: #e0e0e0;
  border-radius: 8px;
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-export {
  width: 80px;
}

.skeleton-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

.skeleton-table-header {
  display: flex;
  gap: 16px;
  margin-bottom: 10px;
}

.skeleton-cell {
  height: 20px;
  background-color: #e0e0e0;
  border-radius: 4px;
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-cell-header {
  width: 150px;
}

.skeleton-table-row {
  display: flex;
  gap: 16px;
  margin-bottom: 10px;
}

@keyframes pulse {
  0% {
    background-color: #e0e0e0;
  }

  50% {
    background-color: #f0f0f0;
  }

  100% {
    background-color: #e0e0e0;
  }
}
</style>
